/**
 * Text Density definitions for children's book pages.
 *
 * This file contains both FE-safe data (title, description, emoji) and BE-only data (prompts).
 * - FE should import: TEXT_DENSITY_KEYS, TEXT_DENSITY_DISPLAY, getTextDensityDisplay
 * - BE should import: TEXT_DENSITY_STYLES, getTextDensityPrompt
 */

/**
 * Text density keys - used for type safety and as enum values.
 * medium is the default option for balanced reading.
 */
export const TEXT_DENSITY_KEYS = ['minimal', 'medium', 'full'] as const;

export type TextDensityKey = (typeof TEXT_DENSITY_KEYS)[number];

/**
 * Full text density definition including prompts (BE use only).
 */
export interface TextDensityDefinition {
  /** Display title for the text density option */
  title: string;
  /** User-friendly description */
  description: string;
  /** Emoji icon for UI display */
  emoji: string;
  /** AI prompt fragment for text density (BE only) */
  densityPrompt: string;
}

/**
 * FE-safe display data (no prompts).
 */
export interface TextDensityDisplay {
  title: string;
  description: string;
  emoji: string;
}

/**
 * Complete text density definitions with prompts.
 * Use this on the backend for story generation.
 */
export const TEXT_DENSITY_STYLES: Record<
  TextDensityKey,
  TextDensityDefinition
> = {
  minimal: {
    title: 'Minimal Text',
    description: 'One short sentence per page — perfect for toddlers',
    emoji: '🌱',
    densityPrompt:
      'Write exactly 1 short, simple sentence per page (maximum 10 words).',
  },
  medium: {
    title: 'Medium Text',
    description: 'A small paragraph — balanced reading',
    emoji: '📖',
    densityPrompt:
      'Write 2–3 sentences per page. Keep language clear and engaging.',
  },
  full: {
    title: 'Full Text',
    description: 'Rich storytelling with detailed narration',
    emoji: '📚',
    densityPrompt:
      'Write 3–4 rich sentences per page with descriptive language and emotion.',
  },
};

/**
 * FE-safe display data without prompts.
 * Use this on the frontend for showing options to users.
 */
export const TEXT_DENSITY_DISPLAY: Record<TextDensityKey, TextDensityDisplay> =
  Object.fromEntries(
    TEXT_DENSITY_KEYS.map((key) => [
      key,
      {
        title: TEXT_DENSITY_STYLES[key].title,
        description: TEXT_DENSITY_STYLES[key].description,
        emoji: TEXT_DENSITY_STYLES[key].emoji,
      },
    ])
  ) as Record<TextDensityKey, TextDensityDisplay>;

/**
 * Get text density prompt by key (BE use only).
 */
export function getTextDensityPrompt(key: TextDensityKey): string {
  return TEXT_DENSITY_STYLES[key].densityPrompt;
}

/**
 * Get text density display data by key (FE-safe).
 */
export function getTextDensityDisplay(key: TextDensityKey): TextDensityDisplay {
  return TEXT_DENSITY_DISPLAY[key];
}
